import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, exhaustMap, filter, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MeasureActions } from '../measure.actions';
import { Store } from '@ngrx/store';
import {
  selectCurrentSurveyVersionId,
  selectMeasureState,
  selectSurveyFlowExpressionAndTypeIdobject,
  selectSurveyFlowLogicTypeQuestionIdobject,
  selectSurveyFlowTree,
} from '../measure.selector';
import { MeasureBlockService } from '../../services/measure-block.service';
import { FlowItemsService } from '../../services/flow-items.service';
import { ApiResponseModel } from '../../../../shared/models/api-response.model';
import { EmbeddedFieldsService } from '../../services/embedded-fields.service';
import { FlowItemBasicModel } from '../../models/api/survey-version-tree.model';
import { exhaustMapLatest } from '../../../../shared/utils/exhaust-map-latest';

@Injectable()
export class FlowEffects {
  currentMeasureVersionId = this.store.select(selectCurrentSurveyVersionId);
  selectSurveyFlowLogicTypeQuestionIdobject$ = this.store.select(selectSurveyFlowLogicTypeQuestionIdobject);
  selectSurveyFlowExpressionAndTypeIdobject$ = this.store.select(selectSurveyFlowExpressionAndTypeIdobject);
  surveyFlowTree$ = this.store.select(selectSurveyFlowTree);
  // static reference data (logic types, operators, ...) is loaded once; repeated dispatches are ignored
  measureState$ = this.store.select(selectMeasureState);

  getLogicTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.getFlowItemLogicTypes),
      withLatestFrom(this.measureState$),
      filter(([, state]) => !state.logicTypes?.length),
      exhaustMap(() =>
        this.flowItemsService.getFlowItemLogicTypes().pipe(
          map((data) => MeasureActions.getFlowItemLogicTypesSuccess({ logicTypes: data.data })),
          catchError(() => of(MeasureActions.getFlowItemLogicTypesFailure())),
        ),
      ),
    ),
  );

  getExpressionTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.getFlowItemExpressionTypes),
      withLatestFrom(this.measureState$),
      filter(([, state]) => !state.expressionTypes?.length),
      exhaustMap(() =>
        this.flowItemsService.getFlowExpressionTypes().pipe(
          map((data) => MeasureActions.getFlowItemExpressionTypesSuccess({ expressionTypes: data.data })),
          catchError(() => of(MeasureActions.getFlowItemExpressionTypesFailure())),
        ),
      ),
    ),
  );

  getFlowOperators$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.getFlowOperators),
      withLatestFrom(this.measureState$),
      filter(([, state]) => !state.flowOperators?.length),
      exhaustMap(() =>
        this.flowItemsService.getFlowOperators().pipe(
          map((data) => MeasureActions.getFlowOperatorsSuccess({ flowOperators: data.data })),
          catchError(() => of(MeasureActions.getFlowOperatorsFailure())),
        ),
      ),
    ),
  );

  getFlowItemTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.getFlowItemTypes),
      withLatestFrom(this.measureState$),
      filter(([, state]) => !state.flowItemTypes?.length),
      exhaustMap(() =>
        this.flowItemsService.getFlowItemTypes().pipe(
          map((data) => MeasureActions.getFlowItemTypesSuccess({ flowItemTypes: data.data })),
          catchError(() => of(MeasureActions.getFlowItemTypesFailure())),
        ),
      ),
    ),
  );

  getFlowConjunctions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.getFlowConjunctions),
      withLatestFrom(this.measureState$),
      filter(([, state]) => !state.flowConjunctions?.length),
      exhaustMap(() =>
        this.flowItemsService.getFlowConjunctions().pipe(
          map((data) => MeasureActions.getFlowConjunctionsSuccess({ flowConjunctions: data.data })),
          catchError(() => of(MeasureActions.getFlowConjunctionsFailure())),
        ),
      ),
    ),
  );

  getFlowAllSurveyVersionQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.getAllSurveyVersionQuestions),
      exhaustMapLatest(({ versionId }) =>
        this.measureBlockService.getListOfAllQuestions(versionId).pipe(
          map((data) => MeasureActions.getAllSurveyVersionQuestionsSuccess({ flowQuestionsList: data.data })),
          catchError(() => of(MeasureActions.getAllSurveyVersionQuestionsFailure())),
        ),
      ),
    ),
  );

  getVersionEmbeddedFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.getVersionEmbeddedFields),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.getVersionFlowEmbeddedFields(surveyVersionId).pipe(
          map((data) => MeasureActions.getVersionEmbeddedFieldsSuccess({ embeddedFields: data.data })),
          catchError(() => of(MeasureActions.getVersionEmbeddedFieldsFailure())),
        ),
      ),
    ),
  );

  addFlowItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addFlowItemToSurvey),
      withLatestFrom(this.currentMeasureVersionId, this.surveyFlowTree$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId, flowTree]) => {
        const knownIds = this.collectFlowItemIds(flowTree);

        return this.flowItemsService.addFlowItemToSurvey(surveyVersionId, action.flowItemTypeId, action.flowItem).pipe(
          switchMap(() => this.positionAddedFlowItem(surveyVersionId, knownIds, action.index)),
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.addFlowItemToSurveyFailure())),
        );
      }),
    ),
  );

  addChildFlowItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addChildFlowItemToSurvey),
      withLatestFrom(this.currentMeasureVersionId, this.surveyFlowTree$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId, flowTree]) => {
        const knownIds = this.collectFlowItemIds(flowTree);

        return this.flowItemsService
          .addChildFlowItemToSurvey(action.flowItemTypeId, action.flowItem, action.parentIdobject)
          .pipe(
            switchMap(() => this.positionAddedFlowItem(surveyVersionId, knownIds, action.index)),
            map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
            catchError(() => of(MeasureActions.addChildFlowItemToSurveyFailure())),
          );
      }),
    ),
  );

  /**
   * The create endpoint always appends, so inserting "between" is a two-step move: re-read the tree,
   * find the item that was not there before the create, and set its 1-based index. Without a target
   * index (the plain "Add New Element Here" button) nothing extra happens.
   */
  private positionAddedFlowItem(
    surveyVersionId: number,
    knownIds: Set<number>,
    index: number | undefined,
  ): Observable<unknown> {
    if (index === undefined) {
      return of(null);
    }

    return this.flowItemsService.getAllFlowItems(surveyVersionId).pipe(
      switchMap((response) => {
        const createdId = this.findCreatedFlowItemId(response.data ?? [], knownIds);
        if (!createdId) {
          return of(null);
        }
        return this.flowItemsService.updateFlowItemInSurvey(createdId, { idobject: createdId, index });
      }),
      catchError(() => of(null)),
    );
  }

  private collectFlowItemIds(flowItems: FlowItemBasicModel[] | null | undefined): Set<number> {
    const ids = new Set<number>();
    const walk = (items: FlowItemBasicModel[] | null | undefined): void => {
      for (const item of items ?? []) {
        ids.add(item.idobject);
        walk(item.flowItems);
      }
    };
    walk(flowItems);
    return ids;
  }

  private findCreatedFlowItemId(flowItems: FlowItemBasicModel[], knownIds: Set<number>): number | null {
    for (const item of flowItems) {
      if (!knownIds.has(item.idobject)) {
        return item.idobject;
      }
      const nested = this.findCreatedFlowItemId(item.flowItems ?? [], knownIds);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  removeFlowItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.removeFlowItem),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.removeFlowItemFromSurvey(action.flowItemId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.removeFlowItemFailure())),
        ),
      ),
    ),
  );

  updateFlowItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateFlowItem),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.updateFlowItemInSurvey(action.flowItemId, action.flowItem).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateFlowItemFailure())),
        ),
      ),
    ),
  );

  changeFlowItemParent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.changeFlowItemParent),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([action, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.changeFlowItemParent(action.flowItemId, action.newParentOid).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.changeFlowItemParentFailure())),
        ),
      ),
    ),
  );

  moveFlowItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.moveFlowItem),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) => {
        const setIndex$ = this.flowItemsService.updateFlowItemInSurvey(action.flowItemId, {
          idobject: action.flowItemId,
          index: action.index,
        });
        // reparent first (when needed), then set the position, then reload once
        const move$ = action.reparent
          ? this.flowItemsService
              .changeFlowItemParent(action.flowItemId, action.newParentOid)
              .pipe(switchMap(() => setIndex$))
          : setIndex$;

        return move$.pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.moveFlowItemFailure())),
        );
      }),
    ),
  );

  addEmbeddedDataOps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addEmbeddedDataOps),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.addEmbeddedDataOpsToSurvey(action.flowItemId, action.embeddedField).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.addEmbeddedDataOpsFailure())),
        ),
      ),
    ),
  );

  updateEmbeddedDataOps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateEmbeddedDataOps),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.updateEmbeddedDataOpsInSurvey(action.embeddedDataOpsId, action.embeddedField).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateEmbeddedDataOpsFailure())),
        ),
      ),
    ),
  );

  deleteEmbeddedDataOps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.deleteEmbeddedDataOps),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.deleteEmbeddedDataOpsFromSurvey(action.embeddedDataOpsId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.deleteEmbeddedDataOpsFailure())),
        ),
      ),
    ),
  );

  addEmbeddedField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addEmbeddedField),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.embeddedFieldsService
          .createEmbeddedField(surveyVersionId, action.choiceName, action.choiceType, action.choiceValue)
          .pipe(
            map(() => MeasureActions.getVersionEmbeddedFields()),
            catchError(() => of(MeasureActions.addEmbeddedFieldFailure())),
          ),
      ),
    ),
  );
  updateEmbeddedField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateEmbeddedField),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.embeddedFieldsService
          .updateEmbeddedField(action.fieldOid, action.choiceName, action.choiceType, action.choiceValue)
          .pipe(
            map(() => MeasureActions.getVersionEmbeddedFields()),
            catchError(() => of(MeasureActions.updateEmbeddedFieldFailure())),
          ),
      ),
    ),
  );
  deleteEmbeddedField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.deleteEmbeddedField),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.embeddedFieldsService.removeEmbeddedFieldFromSurvey(action.embeddedFieldOid).pipe(
          map(() => MeasureActions.getVersionEmbeddedFields()),
          catchError(() => of(MeasureActions.deleteEmbeddedFieldFailure())),
        ),
      ),
    ),
  );

  /* flow items branch logic */
  addExpressionBranchLogic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addExpressionBranchLogic),
      withLatestFrom(this.currentMeasureVersionId, this.selectSurveyFlowLogicTypeQuestionIdobject$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId, flowLogicTypeQuestionIdobject]) =>
        this.flowItemsService
          .addExpression(action.flowItemId, action.expressionType, action.index, action.pointApi)
          .pipe(
            switchMap((response) => {
              const expressionId = response?.data[0]?.idobject;

              if (!expressionId) {
                console.error('Expression id is not set');
                return of(MeasureActions.addExpressionBranchLogicFailure());
              }

              if (!flowLogicTypeQuestionIdobject) {
                console.error('Flow logic type question idobject is not set');
                return of(MeasureActions.addExpressionBranchLogicFailure());
              }

              return this.flowItemsService
                .addSubExpressionBranchLogic(expressionId, flowLogicTypeQuestionIdobject, 0)
                .pipe(
                  map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
                  catchError(() => of(MeasureActions.addExpressionBranchLogicFailure())),
                );
            }),
            catchError(() => of(MeasureActions.addExpressionBranchLogicFailure())),
          ),
      ),
    ),
  );

  updateExpressionBranchLogicType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateExpressionBranchLogicType),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.updateExpressionBranchLogicType(action.expressionId, action.expressionType).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateExpressionBranchLogicTypeFailure())),
        ),
      ),
    ),
  );

  addSubExpressionBranchLogic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addSubExpressionBranchLogic),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService
          .addSubExpressionBranchLogic(action.expressionOid, action.logicType, action.index, action.conjunction)
          .pipe(
            map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
            catchError(() => of(MeasureActions.addSubExpressionBranchLogicFailure())),
          ),
      ),
    ),
  );

  updateSubExpressionBranchLogic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateSubExpressionBranchLogic),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.updateSubExpressionBranchLogic(action.subExpressionId, action.subExpression).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateSubExpressionBranchLogicFailure())),
        ),
      ),
    ),
  );

  deleteSubExpressionBranchLogic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.deleteSubExpression),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.deleteSubExpressionBranchLogic(action.idobject).pipe(
          switchMap((response) => {
            if (action.isLast) {
              return this.flowItemsService.deleteExpressionBranchLogic(action.expressionIdobject).pipe(
                map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
                catchError(() => of(MeasureActions.deleteSubExpressionFailure())),
              );
            } else {
              return of(MeasureActions.getSurveyVersionTree({ surveyVersionId })).pipe(
                catchError(() => of(MeasureActions.deleteSubExpressionFailure())),
              );
            }
          }),
          catchError(() => of(MeasureActions.deleteSubExpressionFailure())),
        ),
      ),
    ),
  );

  deleteExpressionBranchLogic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.deleteExpression),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.flowItemsService.deleteExpressionBranchLogic(action.idobject).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.deleteExpressionFailure())),
        ),
      ),
    ),
  );

  moveSubExpressionToNewExpression$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.moveSubExpressionToNewExpression),
      withLatestFrom(this.currentMeasureVersionId, this.selectSurveyFlowExpressionAndTypeIdobject$),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId, selectSurveyFlowExpressionAndTypeIdobject]) =>
        this.flowItemsService
          .addExpression(
            action.flowItemId,
            selectSurveyFlowExpressionAndTypeIdobject || 0,
            action.lastExpressionIndex,
            action.pointApi,
          )
          .pipe(
            switchMap((res: ApiResponseModel<any>) =>
              this.flowItemsService
                .addSubExpressionBranchLogic(
                  res.data[0].idobject,
                  action.subExpression.logicType?.idobject,
                  0,
                  action.subExpression.conjunction?.idobject,
                  action.subExpression.leftOperand?.toString(),
                  action.subExpression.rightOperand?.toString(),
                  action.subExpression.operator?.idobject,
                )
                .pipe(
                  switchMap(() =>
                    this.flowItemsService.deleteSubExpressionBranchLogic(action.subExpression.idobject || 0).pipe(
                      map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
                      catchError(() => of(MeasureActions.moveSubExpressionToNewExpressionFailure())),
                    ),
                  ),
                ),
            ),
          ),
      ),
    ),
  );

  /* flow items branch logic - end */

  constructor(
    private store: Store,
    private actions$: Actions,
    private measureBlockService: MeasureBlockService,
    private flowItemsService: FlowItemsService,
    private embeddedFieldsService: EmbeddedFieldsService,
  ) {}
}
